import { useCallback, useEffect, useRef, useState } from 'react';
import { GenericCard } from 'components/display/Card/Card';
import { Button } from 'components/ui/Button';
import GenericBadge from 'components/ui/Badge';
import { TitleIconFactory } from 'factories/TitleIconFactory';
import { TitleIconKey } from 'enums/titleIconKey';
import { toasterService } from 'components/feedback/Toaster/toasterService';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { useSingleFlight } from 'hooks/useSingleFlight';
import { NsKey } from 'enums/NsKey';
import { useGroupContractProcess } from '../../contexts/GroupContractProcessContext';
import {
  getGroupContractSignatureStatus,
  sendGroupContractForSignature,
} from '../../api/groupContractSignatureApi';

interface SignContractCardProps {
  /** Disabled tant que les prérequis (CP, souscripteur, mandats) ne sont pas remplis. */
  disabled?: boolean;
}

/** Intervalle du polling de secours du statut de signature (SignalR reste la voie nominale). */
const SIGNATURE_STATUS_POLL_MS = 30_000;

/**
 * Signature du contrat collectif : génère le document (contrat + CG concaténés) et
 * l'envoie au souscripteur via Signaturit. Le champ de signature est posé via l'ancre
 * du PDF. Une fois signé, le contrat passe Actif. Mêmes Header/Body que la card de
 * signature individuelle (GenericCard + TitleIconFactory + GenericBadge + Button).
 */
export const SignContractCard = ({ disabled = true }: SignContractCardProps) => {
  const { t } = useDynamicTranslation([NsKey.Contract]);
  const { groupContractId, dataVersion, refresh } = useGroupContractProcess();

  const [signatureSent, setSignatureSent] = useState(false);
  const [isSigned, setIsSigned] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const status = await getGroupContractSignatureStatus(groupContractId);
      setSignatureSent(status.signatureSent);
      setIsSigned(status.isSigned);
    } catch {
      /* card silencieuse en cas d'échec de chargement */
    }
  }, [groupContractId]);

  // Statut relu au mount puis à chaque refresh global (le back finalise le contrat
  // lors de ce GET si Signaturit a terminé — la notification SignalR `Completed`
  // déclenche donc bien la bascule du badge en « Signé » sans recharger la page).
  useEffect(() => {
    loadStatus();
  }, [loadStatus, dataVersion]);

  // Polling de secours tant que la signature est en attente : couvre une notification
  // SignalR perdue (onglet en veille, reconnexion) sans dépendre d'un reload manuel.
  useEffect(() => {
    if (!signatureSent || isSigned) return undefined;
    const timer = setInterval(loadStatus, SIGNATURE_STATUS_POLL_MS);
    return () => clearInterval(timer);
  }, [signatureSent, isSigned, loadStatus]);

  // Quand le statut passe à signé (polling ou SignalR), rafraîchit le contexte pour
  // propager le nouvel état (stepper, verrouillage, documents) au reste de la page.
  const prevSignedRef = useRef<boolean | null>(null);
  useEffect(() => {
    const prev = prevSignedRef.current;
    prevSignedRef.current = isSigned;
    if (prev === false && isSigned) refresh();
  }, [isSigned, refresh]);

  const sendForSignature = useCallback(async () => {
    if (isSigned) return;

    const isResend = signatureSent;
    const res = await sendGroupContractForSignature(groupContractId);
    if (res.isSuccess === false) {
      toasterService.showToast({
        message: res.errorMessage ?? "Échec de l'envoi en signature.",
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red,
      });
      return;
    }
    toasterService.showToast({
      message: isResend
        ? 'Demande de signature renvoyée au souscripteur.'
        : 'Contrat envoyé en signature au souscripteur.',
      displayType: DisplayTypeEnum.Toaster,
      color: GenericColors.Green,
    });
    // Le refresh met à jour le contexte et incrémente dataVersion → le statut de la
    // card et la section documents se rechargent sans démonter la page.
    await refresh();
  }, [isSigned, signatureSent, groupContractId, refresh]);

  // Un seul envoi en vol : les clics répétés sont ignorés (l'envoi génère le PDF,
  // crée la demande Signaturit et écrit le contrat — un doublon concurrent provoque
  // un conflit de concurrence côté back et une double demande Signaturit).
  const { run: handleClick, isInFlight: loading } = useSingleFlight(sendForSignature);

  // États du badge : pas envoyé / en attente / signé.
  const badge = isSigned
    ? { label: 'Signé', color: GenericColors.Green }
    : signatureSent
    ? { label: 'En attente de signature', color: GenericColors.Orange }
    : { label: 'À envoyer', color: GenericColors.Gray };

  // Le bouton n'apparaît pas quand les prérequis ne sont pas remplis ou quand
  // le contrat est déjà signé (rien à faire de plus côté UI).
  const showButton = !(disabled && !signatureSent) && !isSigned;
  const buttonLabel = signatureSent
    ? 'Renvoyer la demande de signature'
    : 'Envoyer en signature';

  return (
    <GenericCard className="w-full">
      <GenericCard.Header
        disabled={disabled && !signatureSent}
        icon={<TitleIconFactory titleIconKey={TitleIconKey.Handshake} />}
        titleAside={(
          <GenericBadge color={badge.color} label={badge.label} badgeType="status" />
        )}
      >
        {t('GROUP_CONTRACT_PROCESS.SIGN_CONTRACT_CARD.TITLE')}
      </GenericCard.Header>
      {showButton && (
        <GenericCard.Body>
          <div className="text-center">
            <Button color="secondary" round onClick={handleClick} disabled={loading}>
              {buttonLabel}
            </Button>
          </div>
        </GenericCard.Body>
      )}
    </GenericCard>
  );
};
